"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";
import { authApi } from "@/lib/api/auth";
import { selectActiveTenant } from "@/lib/active-tenant";
import { fetchAndSyncPreferences, clearLocalPreferences } from "@/lib/preferences";
import type { UserResponse } from "@/lib/api/types";

interface AuthContextType {
  user: UserResponse | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refreshUser = async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
      // Fetch and sync preferences after successful user load
      await fetchAndSyncPreferences();
    } catch {
      setUser(null);
      authApi.logout();
      clearLocalPreferences();
      // Drop any cached (tenant/user-scoped) data from the invalidated session
      queryClient.clear();
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("access_token");
      if (token) {
        await refreshUser();
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Which workspace an error happened in. Reuses the ['user-tenants'] query the
  // tenant switcher already runs, so this costs no extra request.
  const { data: tenantsData } = useQuery({
    queryKey: ["user-tenants"],
    queryFn: () => authApi.getTenants(),
    enabled: !!user,
  });
  const activeTenant = selectActiveTenant(tenantsData);

  // Keep Sentry user context in sync with authenticated user
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        // Undefined until the tenants query resolves. Previously this read
        // user.tenant_id, which /auth/me has never returned — so every event
        // carried an undefined workspace.
        tenant_id: activeTenant?.id,
        tenant_name: activeTenant?.name,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user, activeTenant?.id, activeTenant?.name]);

  const login = async (email: string, password: string) => {
    // Ensure no previous user's cached data survives into this session
    queryClient.clear();
    const response = await authApi.login({ email, password });
    localStorage.setItem("access_token", response.access_token);
    localStorage.setItem("refresh_token", response.refresh_token);
    await refreshUser();
  };

  const loginWithTokens = async (accessToken: string, refreshToken: string) => {
    // Ensure no previous user's cached data survives into this session
    queryClient.clear();
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    await refreshUser();
  };

  const logout = () => {
    authApi.logout();
    clearLocalPreferences();
    // Drop ALL cached queries (tenant/user-scoped) before leaving the session
    queryClient.clear();
    Sentry.setUser(null);
    // Full reload guarantees no in-memory React/query state survives the identity change
    window.location.href = "/auth/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithTokens, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
